/**
 * Shared runtime utilities for Recharge portal customizations.
 *
 * Exposed as `window.etRechargeUtils` so plain theme assets can reuse
 * consistent helpers without a bundler/build step.
 */
(function bootstrapRechargeUtils() {
  if (window.etRechargeUtils) return;
  let portalSessionPromise = null;

  function parseInteger(rawValue, fallback) {
    const parsed = Number.parseInt(String(rawValue ?? ''), 10);
    if (!Number.isFinite(parsed)) {
      return typeof fallback === 'undefined' ? 0 : fallback;
    }
    return parsed;
  }

  function parseOptionalInteger(rawValue) {
    if (rawValue === null || typeof rawValue === 'undefined') return null;
    const parsed = parseInteger(rawValue, Number.NaN);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return parsed;
  }

  function parseDateValue(rawValue) {
    const raw = String(rawValue || '').trim();
    if (!raw) return null;

    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const year = parseInteger(isoMatch[1], Number.NaN);
      const monthIndex = parseInteger(isoMatch[2], Number.NaN) - 1;
      const day = parseInteger(isoMatch[3], Number.NaN);
      if (
        Number.isFinite(year) &&
        Number.isFinite(monthIndex) &&
        Number.isFinite(day)
      ) {
        const parsed = new Date(year, monthIndex, day);
        if (
          parsed.getFullYear() === year &&
          parsed.getMonth() === monthIndex &&
          parsed.getDate() === day
        ) {
          return parsed;
        }
      }
    }

    const timestamp = Date.parse(raw);
    if (!Number.isFinite(timestamp)) return null;
    const parsed = new Date(timestamp);
    if (!Number.isFinite(parsed.getTime())) return null;
    return parsed;
  }

  function escapeHtml(value) {
    if (value === null || typeof value === 'undefined') return '';
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  function getSharedRechargeSdk() {
    const sharedSdk = window.etRechargeSdk;
    if (
      !sharedSdk ||
      typeof sharedSdk.getMethod !== 'function' ||
      typeof sharedSdk.ready !== 'function'
    ) {
      throw new Error(
        'Shared Recharge SDK is unavailable. Ensure recharge-sdk-bootstrap.js loads first.'
      );
    }
    return sharedSdk;
  }

  function getRechargeMethod(pathSegments) {
    return getSharedRechargeSdk().getMethod(pathSegments);
  }

  function getOptionalRechargeMethod(pathSegments) {
    try {
      return getRechargeMethod(pathSegments);
    } catch (_) {
      return null;
    }
  }

  async function getPortalSession() {
    if (portalSessionPromise) return portalSessionPromise;

    portalSessionPromise = (async function resolvePortalSession() {
      await getSharedRechargeSdk().ready();
      if (window.ReCharge && window.ReCharge.session) {
        const existingSession = window.ReCharge.session;
        if (existingSession && (existingSession.api_token || existingSession.apiToken)) {
          return existingSession;
        }
      }

      const loginCustomerPortal = getRechargeMethod(['auth', 'loginCustomerPortal']);
      return loginCustomerPortal();
    })();

    try {
      return await portalSessionPromise;
    } catch (error) {
      portalSessionPromise = null;
      throw error;
    }
  }

  function clearPortalSession() {
    portalSessionPromise = null;
  }

  function resolveSubscriptionAddressId(subscription) {
    const candidates = [
      subscription?.address_id,
      subscription?.addressId,
      subscription?.address?.id,
      subscription?.address?.address_id,
      subscription?.shipping_address_id,
      subscription?.shippingAddressId,
    ];
    for (const candidate of candidates) {
      const parsed = parseOptionalInteger(candidate);
      if (parsed) return parsed;
    }
    return null;
  }

  function resolveChargeDateValue(charge, subscription) {
    const candidates = [
      charge?.scheduled_at,
      charge?.charge_date,
      charge?.created_at,
      subscription?.next_charge_date,
      subscription?.next_charge_scheduled_at,
      subscription?.next_charge_at,
    ];
    for (const candidate of candidates) {
      const parsed = parseDateValue(candidate);
      if (parsed) return parsed;
    }
    return null;
  }

  function getFocusableElements(container) {
    if (!(container instanceof Element)) return [];
    return Array.from(
      container.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => {
      if (!(el instanceof HTMLElement)) return false;
      if (el.hasAttribute('disabled')) return false;
      if (el.getAttribute('aria-hidden') === 'true') return false;
      return true;
    });
  }

  function trapFocus(container, options) {
    if (!(container instanceof HTMLElement)) return function noop() {};
    const opts = options && typeof options === 'object' ? options : {};
    const onEscape = typeof opts.onEscape === 'function' ? opts.onEscape : null;
    const initialFocus = opts.initialFocus instanceof HTMLElement ? opts.initialFocus : null;

    const keydownHandler = (event) => {
      if (event.key === 'Escape' || event.key === 'Esc') {
        if (onEscape) {
          event.preventDefault();
          onEscape();
        }
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = getFocusableElements(container);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      }
    };

    container.addEventListener('keydown', keydownHandler);
    if (initialFocus) {
      initialFocus.focus();
    } else {
      const focusable = getFocusableElements(container);
      if (focusable.length) focusable[0].focus();
    }

    return function releaseFocusTrap() {
      container.removeEventListener('keydown', keydownHandler);
    };
  }

  window.etRechargeUtils = Object.freeze({
    parseInteger,
    parseOptionalInteger,
    parseDateValue,
    escapeHtml,
    getSharedRechargeSdk,
    getRechargeMethod,
    getOptionalRechargeMethod,
    getPortalSession,
    clearPortalSession,
    resolveSubscriptionAddressId,
    resolveChargeDateValue,
    getFocusableElements,
    trapFocus,
  });
})();
